import React from 'react';
import {
  Tabs,
  Tab,
  Box,
  useTheme,
  useMediaQuery,
  SvgIconProps,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStudioPermissions } from '../../../../hooks/useStudioPermissions';
import { StudioPermissions } from '../../../../types/studio';

/**
 * Studio section configuration
 */
interface StudioSection {
  id: string;
  name: string;
  path: string;
  icon: React.ComponentType<SvgIconProps>;
  requiredPermissions: (keyof StudioPermissions)[];
}

const studioSections: StudioSection[] = [
  {
    id: 'overview',
    name: 'Overview',
    path: '',
    icon: DashboardIcon,
    requiredPermissions: ['canViewStudio'],
  },
  {
    id: 'members',
    name: 'Members',
    path: '/members',
    icon: PeopleIcon,
    requiredPermissions: ['canManageMembers'],
  },
  {
    id: 'schedule',
    name: 'Schedule',
    path: '/schedule',
    icon: CalendarIcon,
    requiredPermissions: ['canViewStudio'],
  },
  {
    id: 'settings',
    name: 'Settings',
    path: '/settings',
    icon: SettingsIcon,
    requiredPermissions: ['canEditStudio'],
  },
  {
    id: 'tools',
    name: 'Tools',
    path: '/tools',
    icon: BuildIcon,
    requiredPermissions: ['canAccessTools'],
  },
];

/**
 * Props for StudioTabs component
 */
interface StudioTabsProps {
  /** Current studio ID */
  studioId: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Studio section navigation tabs component
 *
 * Provides tabbed navigation for studio sections with permission-based visibility
 * and responsive design for mobile/desktop.
 */
export const StudioTabs: React.FC<StudioTabsProps> = ({
  studioId,
  className,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useStudioPermissions();

  /**
   * Get current active section from URL
   */
  const getCurrentSection = (): string => {
    const pathParts = location.pathname.split('/');
    // URL format: /studio/:studioId/:section
    return pathParts[3] || 'overview';
  };

  /**
   * Get available sections based on user permissions
   */
  const getAvailableSections = (): StudioSection[] =>
    studioSections.filter(section =>
      section.requiredPermissions.every(permission => hasPermission(permission))
    );

  /**
   * Handle tab change
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    const section = newValue === 'overview' ? '' : `/${newValue}`;
    navigate(`/studio/${studioId}${section}`);
  };

  const availableSections = getAvailableSections();
  const currentSection = getCurrentSection();

  // Don't render if no sections available
  if (availableSections.length === 0) {
    return null;
  }

  return (
    <Box
      className={className}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Tabs
        value={currentSection}
        onChange={handleTabChange}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : false}
        allowScrollButtonsMobile
        sx={{
          minHeight: 48,
          '& .MuiTab-root': {
            minHeight: 48,
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            minWidth: isMobile ? 'auto' : 120,
            px: isMobile ? 2 : 3,
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 600,
            },
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        }}
      >
        {availableSections.map(section => {
          const IconComponent = section.icon;

          return (
            <Tab
              key={section.id}
              value={section.id}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: '1.125rem',
                      ...(isMobile && { fontSize: '1rem' }),
                    }}
                  />
                  {!isMobile && section.name}
                </Box>
              }
              aria-label={`Navigate to ${section.name} section`}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default StudioTabs;
